import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert, Tooltip, TextField,
} from '@mui/material';
import { FileDownload as DownloadIcon, InfoOutlined, WarningAmber } from '@mui/icons-material';
import { getW2DeliveryStatus, releaseW2s, downloadW2DeliveryCsv, withdrawW2ConsentAsAdmin } from '../api';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const PAPER_REASONS = {
  no_consent: 'No consent',
  withdrawn: 'Consent withdrawn',
  account_inactive: 'Account deactivated',
  not_notified: 'Not notified — release again',
  notice_pending: 'Courtesy copy not sent — release again',
  substitute_not_compliant: 'Electronic delivery off - give a paper W-2',
  access_lost: 'Account deactivated - send paper',
};

export default function W2DeliveryPanel({ taxYear, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [wdRow, setWdRow] = useState(null);
  const [wdNote, setWdNote] = useState('');
  const [wdReceived, setWdReceived] = useState('');
  const [wdSaving, setWdSaving] = useState(false);
  const [wdError, setWdError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getW2DeliveryStatus(taxYear);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load W-2 delivery status');
    }
  }, [taxYear]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const summary = data?.summary || { total: 0, electronic: 0, courtesy_copies: 0, paper_required: 0, not_released: 0, needs_rerelease: 0, email_failed: 0 };
  const flagOn = !!data?.electronic_furnishing_enabled;
  const rows = data?.rows || [];
  const pending = rows.filter(r => r.delivery === 'not_released' || r.delivery === 'needs_rerelease' || r.paper_reason === 'not_notified' || r.notice_pending).length;

  const handleRelease = async () => {
    setReleasing(true); setError('');
    try {
      const res = await releaseW2s(taxYear);
      setConfirmOpen(false);
      setSuccess(res.data.courtesy_copies_posted !== undefined && res.data.courtesy_copies_posted !== null
        ? `Released ${res.data.released} W-2(s): ${res.data.courtesy_copies_posted} courtesy copies posted, ${res.data.paper_required} need paper`
        : `Released ${res.data.released} W-2(s): ${res.data.furnished_electronically} furnished electronically, ${res.data.paper_required} need paper`);
      await load();
    } catch (err) {
      setConfirmOpen(false);
      setError(err.response?.data?.error || 'Failed to release W-2s');
    } finally {
      setReleasing(false);
    }
  };

  const handleCsv = async () => {
    try {
      await downloadW2DeliveryCsv(taxYear);
    } catch (err) {
      setError('Failed to export CSV');
    }
  };

  const openWithdraw = (r) => { setWdRow(r); setWdNote(''); setWdReceived(''); setWdError(''); };
  const handleAdminWithdraw = async () => {
    setWdSaving(true); setWdError('');
    try {
      await withdrawW2ConsentAsAdmin(wdRow.employee_id, wdNote.trim(), wdReceived || undefined);
      setWdRow(null);
      setSuccess(`Recorded written withdrawal for ${wdRow.first_name} ${wdRow.last_name}`);
      await load();
    } catch (err) {
      setWdError(err.response?.data?.error || 'Failed to record withdrawal');
    } finally {
      setWdSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h6">W-2 Delivery — {taxYear}</Typography>
          <Tooltip title="Who gets this year's W-2 electronically (they consented in My Pay) and who still needs a paper copy. Release posts the generated W-2s to consenting employees (as courtesy copies until electronic delivery is turned on) and sends the notice by email and in-app. A W-2 you regenerate after releasing is a corrected statement — release again so it is furnished again.">
            <InfoOutlined fontSize="small" color="action" />
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" disabled={pending <= 0} onClick={() => setConfirmOpen(true)}>
            Release {taxYear} W-2s to employees
          </Button>
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleCsv}>Export CSV</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Chip size="small" label={`${summary.total} W-2s`} />
        {flagOn
          ? <Chip size="small" label={`${summary.electronic} electronic`} />
          : <Chip size="small" label={`${summary.courtesy_copies} courtesy copies posted`} />}
        <Chip size="small" color={summary.paper_required > 0 ? 'warning' : 'default'} label={`${summary.paper_required} need paper`} />
        <Chip size="small" label={`${summary.not_released} not released`} />
        <Chip size="small" color={summary.needs_rerelease > 0 ? 'error' : 'default'} label={`${summary.needs_rerelease} need re-release`} />
      </Box>

      {data && !data.electronic_furnishing_enabled && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Electronic delivery is off. Until it is turned on, give every released employee a paper W-2 (consenting employees included); the PDFs employees download here are courtesy copies, labeled "copy for your records".
        </Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 1 }}>{success}</Alert>}
      {summary.email_failed > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Notice email failed for {summary.email_failed} employee(s). Their W-2 is posted in My Pay and they got an in-app notice, but the IRS notice must go by email, mail or in person — check Email Settings or deliver the notice another way.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Consent</TableCell>
              <TableCell>W-2</TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell>Downloaded</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No W-2s generated for {taxYear}. Use Generate Form → Form W-2 — All Employees first.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map(r => (
              <TableRow key={r.filing_id} hover>
                <TableCell>
                  {r.last_name}, {r.first_name}
                  {(!r.employee_active || !r.login_active) && <Chip size="small" label="Deactivated" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>
                  {r.consent === 'consented' ? `Consented ${fmtDate(r.consent_at)}`
                    : r.consent === 'withdrawn' ? `Withdrawn ${fmtDate(r.consent_at)}` : 'Not given'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">Generated {fmtDate(r.generated_at)}</Typography>
                  {r.delivery === 'not_released' ? (
                    <Typography variant="caption" color="text.secondary">Not released</Typography>
                  ) : r.delivery === 'needs_rerelease' ? (
                    <Chip size="small" color="error" label="Needs re-release" />
                  ) : (
                    <Typography variant="caption" color="text.secondary">Released {fmtDate(r.released_at)}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {r.delivery === 'electronic' ? (
                    <>
                      <Chip size="small" color="success" label="Electronic" />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Notified {fmtDate(r.furnished_at)}
                        {r.email_sent === false && (
                          <Tooltip title="Notice email failed">
                            <WarningAmber fontSize="inherit" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                          </Tooltip>
                        )}
                      </Typography>
                    </>
                  ) : r.delivery === 'paper' ? (
                    <>
                      <Chip size="small" color="warning" label="Paper required" />
                      {!flagOn && r.courtesy_copy_posted && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>PDF courtesy copy posted</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {PAPER_REASONS[r.paper_reason] || ''}
                      </Typography>
                      {r.notice_pending && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                          {PAPER_REASONS.notice_pending}
                        </Typography>
                      )}
                    </>
                  ) : '—'}
                </TableCell>
                <TableCell>{fmtDate(r.downloaded_at)}</TableCell>
                <TableCell>
                  {r.consent === 'consented' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Button size="small" color="inherit" onClick={() => openWithdraw(r)}>Record written withdrawal</Button>
                      <Tooltip title="Use this when the employee asked you in writing (on paper or by email) to stop electronic W-2 delivery. It takes effect now and later W-2s go on paper.">
                        <InfoOutlined fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!wdRow} onClose={() => setWdRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record written withdrawal</DialogTitle>
        <DialogContent>
          {wdRow && <Typography variant="body2" sx={{ mb: 1.5 }}>{wdRow.first_name} {wdRow.last_name}</Typography>}
          <TextField
            label="Note"
            required
            multiline
            minRows={3}
            fullWidth
            size="small"
            value={wdNote}
            onChange={(e) => setWdNote(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Describe how and when the employee asked in writing to stop electronic delivery"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
            <TextField
              label="Date received"
              type="date"
              size="small"
              value={wdReceived}
              onChange={(e) => setWdReceived(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toLocaleDateString('en-CA') }}
            />
            <Tooltip title="Date the employer received the written withdrawal - the date it takes effect. Leave blank if it is today.">
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Box>
          {wdError && <Alert severity="error" sx={{ mt: 1 }}>{wdError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWdRow(null)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={wdSaving || !wdNote.trim()} onClick={handleAdminWithdraw}>Record withdrawal</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Release {pending} W-2(s) for {taxYear}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {flagOn
              ? 'Employees who consented get an email and in-app notice now and can download the PDF from My Pay. Everyone else needs a paper W-2 by January 31 — Export CSV lists them.'
              : 'Electronic delivery is not on yet. Employees who consented get a courtesy PDF copy in My Pay, but everyone released, consenting employees included, still needs the official W-2 on paper by January 31 — Export CSV lists them.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={releasing} onClick={handleRelease}>Release</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
